import { useCallback, useContext, useState } from 'react';
import { useSelector } from 'react-redux';

import {
	GdsServicesWithPurchasePredictionsFragment,
	PaymentStatusEnum,
	useOrderServicesWithPurchasePredictionQuery
} from '@websky/graphql';
import { getIsPriceFreezeProductSelected, getOrderId, getPaymentStatus } from '../Checkout/store/order/selectors';
import { SelectedService } from './types';
import { ServicePayload } from '../Checkout/store/selectedServices/actions';
import { useConfig } from '../context';
import { getServiceWithMaxPrediction as getServiceWithMaxPredictionUtil } from './utils';
import { VarioqubContext } from '../Varioqub/varioqub';
import { StepsEnum } from '../types';

export const useAiPurchasePredictionService = () => {
	const orderId = useSelector(getOrderId);
	const isPriceFreezeProductSelected = useSelector(getIsPriceFreezeProductSelected);

	const paymentStatus = useSelector(getPaymentStatus);

	const varioqubData = useContext(VarioqubContext);

	const [service, setService] = useState<GdsServicesWithPurchasePredictionsFragment>(null);
	const [isAddedService, setIsAddedService] = useState(false);

	const {
		global: { purchasePredictionThreshold, purchasePredictionThresholdMax }
	} = useConfig();

	const { refetch: getOrderWithPurchasePrediction } = useOrderServicesWithPurchasePredictionQuery({
		skip: true,
		fetchPolicy: 'no-cache'
	});

	const onClearService = useCallback(() => setService(null), [setService]);

	const onDisagree = useCallback(
		async (callback?: () => Promise<void>) => {
			onClearService();

			await callback?.();
		},
		[onClearService]
	);

	const onChange = useCallback(
		(payload: SelectedService, callback?: (payload: SelectedService) => void) => {
			onClearService();
			callback?.(payload);
		},
		[onClearService]
	);

	const onAgree = useCallback(
		async (service: ServicePayload, callback?: (service: ServicePayload) => Promise<void>) => {
			await callback?.(service);

			setIsAddedService(true);
			onClearService();
		},
		[setIsAddedService, onClearService]
	);

	const onContinue = useCallback(
		async (currentStepType: StepsEnum | undefined, callback?: () => Promise<void>) => {
			const blockSmartBoxPopup = isPriceFreezeProductSelected && paymentStatus === PaymentStatusEnum.None;
			if (varioqubData?.get('SmartBox') && !blockSmartBoxPopup && currentStepType === StepsEnum.Order) {
				const { data } = await getOrderWithPurchasePrediction({ id: orderId });
				if (data?.Order) {
					const serviceWithMaxPrediction = getServiceWithMaxPredictionUtil(data.Order.additionalServices);
					const probability = serviceWithMaxPrediction?.aiPurchasePrediction[0].probability;
					if (
						probability >= purchasePredictionThreshold &&
						(!purchasePredictionThresholdMax || probability <= purchasePredictionThresholdMax)
					) {
						setService(serviceWithMaxPrediction);

						return;
					}
				}
			}

			await callback?.();
		},
		[
			orderId,
			paymentStatus,
			isPriceFreezeProductSelected,
			purchasePredictionThreshold,
			getOrderWithPurchasePrediction,
			varioqubData
		]
	);

	return {
		// data
		service,
		isOpen: !!service,
		isAddedService,

		// methods
		onClearService,
		onDisagree,
		onChange,
		onAgree,
		onContinue
	};
};
